import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import { ArrowUpRight, ArrowsClockwise, CheckSquare, Square, Sparkle, Target, Compass, Bell } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAI } from "@/context/AIContext";
import { useAuth } from "@/context/AuthContext";
import RoadmapStrip from "@/components/RoadmapStrip";
import LocationBadge from "@/components/LocationBadge";

// Extract a short human label from a stage name for inline references.
// Mirrors the logic in RoadmapStrip so users see consistent terminology.
function shortStageLabel(name, index) {
  if (!name) return `Etapa ${index + 1}`;
  const firstPart = String(name).trim().split(/[:·\-–—]/)[0].trim();
  if (firstPart && firstPart.length <= 16) return firstPart;
  return `Etapa ${index + 1}`;
}

function computeCurrentIndex(stages = []) {
  const idx = stages.findIndex((s) => s.status !== "completada");
  return idx; // -1 when every stage is completada
}

function ProximasAcciones() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openDrawer } = useAI();

  const load = useCallback(() => {
    setLoading(true);
    api.post("/ai/next-actions", {})
      .then((r) => setActions(r.data.actions || []))
      .catch(() => toast.error("No se pudieron generar las próximas acciones"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="proximas-acciones" className="mb-10">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="label-mono text-muted-foreground">Enfócate aquí</div>
          <h2 className="display text-3xl md:text-4xl mt-1">Próximas 3 Acciones</h2>
        </div>
        <button
          onClick={load}
          className="btn-secondary flex items-center gap-2"
          disabled={loading}
          data-testid="btn-refresh-actions"
        >
          <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          Recalcular
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {(loading && actions.length === 0
          ? [0, 1, 2]
          : actions
        ).map((a, i) => (
          <div key={i} className="brut-cta" data-testid={`cta-proxima-accion-${i + 1}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="label-mono text-muted-foreground">Acción {i + 1}</div>
              <Target size={18} weight="bold" />
            </div>
            {loading && actions.length === 0 ? (
              <div className="h-16 bg-muted animate-pulse" />
            ) : (
              <p className="text-lg font-semibold leading-snug">{a}</p>
            )}
            <button
              onClick={() => openDrawer({ prefill: `Ayúdame a ejecutar esta acción hoy: "${a}"` })}
              className="mt-5 btn-ghost flex items-center gap-2 -mx-2"
              data-testid={`btn-ask-ai-accion-${i + 1}`}
              disabled={loading}
            >
              <Sparkle size={14} weight="bold" />
              Obtener Ayuda
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Hoy() {
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openDrawer } = useAI();
  const { user } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, tk] = await Promise.all([api.get("/overview"), api.get("/tasks")]);
      setOverview(ov.data);
      setTasks(tk.data.tasks || []);
    } catch {
      toast.error("Error al cargar el panel");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTask = async (t) => {
    const next = t.status === "completada" ? "pendiente" : "completada";
    try {
      await api.put(`/tasks/${t.id}`, { status: next });
      setTasks((ts) => ts.map((x) => x.id === t.id ? { ...x, status: next } : x));
    } catch {
      toast.error("No se pudo actualizar la tarea");
    }
  };

  const pending = tasks.filter((t) => t.status !== "completada").slice(0, 6);
  const active = overview?.active_playbook;
  const currentStage = overview?.current_stage;
  const nextMilestone = overview?.next_milestone;
  const firstName = (user?.name || "").split(" ")[0];

  // Locate the current stage's full record inside active_playbook so we can
  // count remaining milestones and preview the next stage's short label.
  const stages = active?.stages || [];
  const currentIdx = computeCurrentIndex(stages);
  const currentStageFull = currentIdx >= 0 ? stages[currentIdx] : null;
  const remainingInStage = currentStageFull
    ? currentStageFull.milestones.filter((m) => !m.completed).length
    : 0;
  const nextStage = currentIdx >= 0 && currentIdx + 1 < stages.length ? stages[currentIdx + 1] : null;
  const nextStageLabel = nextStage ? shortStageLabel(nextStage.name, currentIdx + 1) : null;

  // Toast when a new stage actually unlocks (previous current stage → completed
  // AND currentStage.stage_id changed to a new value). We track the last seen
  // stage id per playbook. The very first non-null id is treated as "initial
  // load", not a transition, to avoid a false-positive toast on mount.
  const lastStageIdRef = useRef(null);
  useEffect(() => {
    const sid = currentStage?.stage_id || null;
    if (!sid) return; // wait until we have real data
    if (lastStageIdRef.current === null) {
      lastStageIdRef.current = sid;
      return;
    }
    if (sid !== lastStageIdRef.current) {
      const unlockedLabel = shortStageLabel(currentStage?.stage_name, currentIdx >= 0 ? currentIdx : 0);
      toast.success(`Etapa desbloqueada: ${unlockedLabel}`, {
        description: "Sigue avanzando — completa los hitos de esta etapa.",
        duration: 5000,
      });
    }
    lastStageIdRef.current = sid;
  }, [currentStage?.stage_id, currentStage?.stage_name, currentIdx]);

  return (
    <div data-testid="page-hoy">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="label-mono text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Hoy</span>
            <span aria-hidden>·</span>
            <LocationBadge variant="tag" testid="hoy-location-badge" />
          </div>
          <h1 className="display text-4xl sm:text-5xl md:text-6xl mt-1 leading-[0.95]">
            {firstName ? `Hola, ${firstName}.` : "¿Cuál es el siguiente paso?"}
          </h1>
          {firstName && (
            <p className="mt-2 text-sm text-muted-foreground">¿Cuál es el siguiente paso?</p>
          )}
        </div>
      </header>

      <Link
        to="/brujula"
        data-testid="banner-brujula"
        className="brut-cta mb-8 flex items-center justify-between gap-4 group"
        style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 border-2 border-foreground flex items-center justify-center bg-background text-foreground flex-shrink-0">
            <Compass size={22} weight="bold" />
          </div>
          <div>
            <div className="label-mono opacity-80">Brújula</div>
            <div className="display text-xl md:text-2xl leading-tight">¿Voy por el camino correcto?</div>
          </div>
        </div>
        <ArrowUpRight size={22} weight="bold" className="flex-shrink-0" />
      </Link>

      <ProximasAcciones />

      <div className="grid lg:grid-cols-3 gap-5 mb-10">
        <div className="brut-card lg:col-span-2" data-testid="etapa-actual">
          <div className="label-mono text-muted-foreground">Etapa actual</div>
          {currentStage ? (
            <>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <h3 className="display text-2xl">{currentStage.stage_name}</h3>
                <StatusPill value={currentStage.status} testid="etapa-status" />
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Ruta: <span className="font-semibold text-foreground">{currentStage.playbook_name}</span>
              </div>
              {nextMilestone && (
                <div className="mt-5 border-t-2 border-foreground pt-4">
                  <div className="label-mono text-muted-foreground">Siguiente hito</div>
                  <div className="mt-1 text-lg font-semibold">{nextMilestone.milestone_name}</div>
                </div>
              )}
              {remainingInStage === 1 && nextStageLabel && (
                <div
                  className="mt-4 border-2 border-foreground bg-primary text-primary-foreground p-3 flex items-start gap-2 shadow-[3px_3px_0_0_hsl(var(--foreground))]"
                  data-testid="stage-unlock-hint"
                >
                  <Bell size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <div className="text-sm font-semibold leading-snug">
                    Te falta 1 hito para desbloquear <span className="whitespace-nowrap">{nextStageLabel}</span>.
                  </div>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={`/playbooks/${currentStage.playbook_id}`}
                  className="btn-primary flex items-center gap-2"
                  data-testid="btn-ir-a-playbook"
                >
                  Abrir playbook <ArrowUpRight size={14} weight="bold" />
                </Link>
                <button
                  onClick={() =>
                    openDrawer({ prefill: `Estoy en la etapa "${currentStage.stage_name}" del playbook "${currentStage.playbook_name}". ¿Cuál es mi siguiente paso concreto?` })
                  }
                  className="btn-secondary flex items-center gap-2"
                  data-testid="btn-preguntar-ia-etapa"
                >
                  <Sparkle size={14} weight="bold" /> Obtener ayuda con este paso
                </button>
              </div>
            </>
          ) : (
            <div className="mt-3 text-sm text-muted-foreground">
              No hay ruta activa todavía. Empieza una en <Link to="/explorar" className="underline font-semibold">Explorar Rutas</Link>.
            </div>
          )}
        </div>

        <div className="brut-card" data-testid="progreso-general">
          <div className="label-mono text-muted-foreground">Progreso general</div>
          <div className="display text-6xl mt-2 leading-none">{overview?.progress_pct ?? 0}%</div>
          <div className="mt-3 h-3 w-full border-2 border-foreground">
            <div
              className="h-full bg-primary"
              style={{ width: `${overview?.progress_pct ?? 0}%` }}
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="label-mono text-muted-foreground">Etapas</div>
              <div className="font-semibold text-lg">{overview?.completed_stages ?? 0}/{overview?.total_stages ?? 0}</div>
            </div>
            <div>
              <div className="label-mono text-muted-foreground">Hitos</div>
              <div className="font-semibold text-lg">{overview?.completed_milestones ?? 0}/{overview?.total_milestones ?? 0}</div>
            </div>
          </div>
          {overview?.compliance_summary && !overview.compliance_summary.early && overview.compliance_summary.total > 0 && (
            <div
              className="mt-4 border-t-2 border-foreground pt-3 flex items-center justify-between gap-2"
              data-testid="hoy-compliance-inline"
            >
              <div className="min-w-0">
                <div className="label-mono text-muted-foreground">Requisitos locales</div>
                <div className="text-sm font-semibold mt-0.5 truncate">
                  {overview.compliance_summary.pending} pendiente{overview.compliance_summary.pending === 1 ? "" : "s"}
                </div>
              </div>
              {active && (
                <Link
                  to={`/playbooks/${active.id}#requisitos-locales`}
                  className="btn-ghost text-xs"
                  data-testid="btn-open-compliance"
                >
                  Ver
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="mb-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="label-mono text-muted-foreground">Enfocado</div>
            <h2 className="display text-3xl">Tareas Pendientes</h2>
          </div>
          <div className="label-mono text-muted-foreground">{pending.length} activas</div>
        </div>
        <div className="brut-card !p-0 divide-y-2 divide-foreground" data-testid="tareas-pendientes">
          {loading && (
            <div className="p-4 text-sm text-muted-foreground">Cargando…</div>
          )}
          {!loading && pending.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">Todo al día. Genera tu próxima acción arriba.</div>
          )}
          {pending.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-4" data-testid={`task-${t.id}`}>
              <button
                onClick={() => toggleTask(t)}
                className="hover:opacity-80"
                data-testid={`toggle-task-${t.id}`}
                aria-label="Marcar tarea"
              >
                {t.status === "completada"
                  ? <CheckSquare size={22} weight="fill" />
                  : <Square size={22} weight="bold" />
                }
              </button>
              <div className="flex-1">
                <div className="font-semibold">{t.title}</div>
                {t.notes && <div className="text-xs text-muted-foreground mt-0.5">{t.notes}</div>}
              </div>
              <StatusPill value={t.priority} />
            </div>
          ))}
        </div>
      </section>

      {active && (
        <section className="mb-10">
          <div className="label-mono text-muted-foreground mb-2">Tu recorrido</div>
          <RoadmapStrip
            stages={active.stages}
            currentIndex={computeCurrentIndex(active.stages)}
            testid="hoy-roadmap"
          />
          <Link
            to={`/playbooks/${active.id}`}
            className="brut-card mt-3 flex items-center justify-between hover:bg-muted transition-colors"
            data-testid="workflow-activo"
          >
            <div>
              <div className="display text-2xl">{active.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{active.category} · {active.stages.length} etapas</div>
            </div>
            <ArrowUpRight size={22} weight="bold" />
          </Link>
        </section>
      )}
    </div>
  );
}
